<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from 'vue-i18n'
import { type RouteRecordNameGeneric, useRoute, useRouter } from 'vue-router'

import HeaderFlowDetails from '@/components/layouts/header/HeaderFlowDetails.vue'
import HeaderMenu from '@/components/layouts/header/HeaderMenu.vue'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { routeNames } from '@/router/routeNames'

const route = useRoute()
const router = useRouter()
const { te, t } = useI18n()

interface CrumbRecord {
  label: string
  to: RouteRecordNameGeneric | null
  href: string
}

const crumbs = computed<CrumbRecord[]>(() => {
  const acc: CrumbRecord[] = []
  const routeSplit = route.path.split('/').filter(el => el !== '')

  for (let i = 0; i < routeSplit.length; i++) {
    const prev = acc[i - 1]
    const route = routeSplit[i]

    const routeHref = prev ? `${prev.href}/${route}` : `/${route}`
    const resolvedRoute = router.resolve(routeHref)

    const transExists = te(`common.route.${String(resolvedRoute.name)}`)
    const label = transExists
      ? t(`common.route.${String(resolvedRoute.name)}`)
      : String(resolvedRoute.name)

    if (!prev) {
      acc.push({
        label: label,
        href: routeHref,
        to: resolvedRoute.name,
      })
      continue
    }

    const prevRoute = router.resolve(prev.href)

    if (prevRoute.name === resolvedRoute.name) {
      continue
    }

    acc.push({
      label: label,
      href: routeHref,
      to: resolvedRoute.name,
    })
  }

  return acc.filter(el => el.to !== undefined)
})
</script>

<template>
  <Breadcrumb v-if="crumbs.length > 0" data-testid="breadcrumbs">
    <BreadcrumbList>
      <HeaderMenu />
      <BreadcrumbItem
        v-for="(i, idx) in crumbs"
        :key="idx"
        data-testid="breadcrumbs-item"
      >
        <BreadcrumbSeparator
          v-if="idx !== crumbs.length && idx !== 0"
          class="-ml-1"
        />
        <BreadcrumbPage class="text-base font-semibold tracking-tight">
          <HeaderFlowDetails v-if="i.to === routeNames.Configuration" />

          <RouterLink
            v-else
            class="hover:border-b-2"
            :to="i.to ? { name: i.to } : i.href"
            :class="{ 'text-muted-foreground': i.to === routeNames.Dashboard }"
          >
            {{ i.label }}
          </RouterLink>
        </BreadcrumbPage>
      </BreadcrumbItem>
    </BreadcrumbList>
  </Breadcrumb>
</template>
